export const loadFont = (fontName: string, fontUrl: string, callback: any) => {
  if (document.fonts && document.fonts.check(`1em ${fontName}`)) {
    console.log(`${fontName} is already loaded`);
    if (callback) callback();
    return;
  }

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = fontUrl;

  document.head.appendChild(link);

  link.onload = () => {
    console.log(`${fontName} font loaded successfully`);
    document.documentElement.classList.add(`font-${fontName.toLowerCase()}`);
    if (callback) callback();
  };

  link.onerror = () => {
    console.error(`Failed to load ${fontName} font`);
  };
};

export const loadGoogleFont = (
  fontName: string,
  fontUrl: string,
  callback?: () => void
) => {
  cacheFont(fontUrl, fontName)
    .then(() => {
      const font = new FontFace(fontName, `url(${fontUrl})`);
      font.load().then((loadedFont) => {
        document.fonts.add(loadedFont);
        console.log(`${fontName} font loaded successfully`);
        document.documentElement.classList.add(
          `font-${fontName.toLowerCase().replace(/\s+/g, "-")}`
        );
        if (callback) callback();
      });
    })
    .catch((err) => {
      console.error(`Failed to load and cache ${fontName} font`, err);
    });
};

function cacheFont(fontUrl: string, cacheKey: string): Promise<Uint8Array> {
  return new Promise(async (resolve, reject) => {
    const dbRequest = indexedDB.open("FontCacheDB", 1);

    dbRequest.onupgradeneeded = (event: any) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains("fonts")) {
        db.createObjectStore("fonts");
      }
    };

    dbRequest.onsuccess = async (event: any) => {
      const db = event.target.result;
      const tx = db.transaction("fonts", "readonly");
      const store = tx.objectStore("fonts");
      const getRequest = store.get(cacheKey);
      getRequest.onsuccess = async () => {
        if (getRequest.result) {
          console.log(`Font "${cacheKey}" loaded from IndexedDB.`);
          resolve(new Uint8Array(getRequest.result));
        } else {
          try {
            const fontResponse = await fetch(fontUrl);
            if (!fontResponse.ok)
              throw new Error(
                `Failed to fetch font: ${fontResponse.statusText}`
              );
            const fontFile = await fontResponse.arrayBuffer();
            const tx = db.transaction("fonts", "readwrite");
            const store = tx.objectStore("fonts");
            store.put(fontFile, cacheKey);
            console.log(`Font "${cacheKey}" cached in IndexedDB.`);
            resolve(new Uint8Array(fontFile));
          } catch (err) {
            reject(err);
          }
        }
      };
      getRequest.onerror = () => reject(getRequest.error);
    };
    dbRequest.onerror = () => reject(dbRequest.error);
  });
}

const googleFontsBaseurl =
  "https://cdn.jsdelivr.net/gh/google/fonts@main/apache";
const googleFontsArabicBaseurl =
  "https://cdn.jsdelivr.net/gh/googlefonts/tajawal@main/fonts";

export const fontList = [
  {
    name: "Aclonica",
    url: `${googleFontsBaseurl}/aclonica/Aclonica-Regular.ttf`,
  },
  {
    name: "Tajawal",
    url: `${googleFontsArabicBaseurl}/ttf/Tajawal-Regular.ttf`,
  },
  {
    name: "Arimo",
    url: `${googleFontsBaseurl}/arimo/Arimo[wght].ttf`,
  },
  {
    name: "Calligraffitti",
    url: `${googleFontsBaseurl}/calligraffitti/Calligraffitti-Regular.ttf`,
  },
  {
    name: "Cherry Cream Soda",
    url: `${googleFontsBaseurl}/cherrycreamsoda/CherryCreamSoda-Regular.ttf`,
  },
  {
    name: "Chewy",
    url: `${googleFontsBaseurl}/chewy/Chewy-Regular.ttf`,
  },
  {
    name: "Coming Soon",
    url: `${googleFontsBaseurl}/comingsoon/ComingSoon-Regular.ttf`,
  },
  {
    name: "Cousine",
    url: `${googleFontsBaseurl}/cousine/Cousine-Regular.ttf`,
  },
  {
    name: "Crafty Girls",
    url: `${googleFontsBaseurl}/craftygirls/CraftyGirls-Regular.ttf`,
  },
  {
    name: "Creepster Caps",
    url: `${googleFontsBaseurl}/creepstercaps/CreepsterCaps-Regular.ttf`,
  },
];

export const loadGoogleFontMethod = () => {
  const fontPromises = fontList.map(
    (font) =>
      new Promise<void>((resolve) => {
        loadGoogleFont(font.name, font.url, () => {
          console.log(`${font.name} font is now available for preview!`);
          resolve();
        });
      })
  );

  Promise.all(fontPromises).then(() => {
    console.log("✅ All fonts are loaded!");
  });
};
